import type { SiteOrder, SiteOrderLine } from '@api/vendor-portal';

import { type ColumnKey } from './cardColumns';
import { remaining } from './orderTotals';

/**
 * The site's open work, read the other way up: one group per product, holding the orders that
 * asked for it.
 *
 * THE SAME LINES, REGROUPED. Nothing is fetched and nothing is summarised away — every order
 * line is still on the page, just filed under its product instead of under its order. A card
 * and its children therefore cannot disagree, because the children ARE the parent's inputs.
 *
 * WHY IT EARNS ITS PLACE. An order card answers "how is this order going". A factory floor asks
 * the other question first: "how much of this thing do I owe, and by when" — and today that
 * means opening six cards and adding up by eye. A site holding 500 finished jerseys has no way
 * to see, at a glance, whether that clears its commitments.
 */

/** One order's claim on a product: the line, plus the header facts a row needs to act on. */
export interface SkuOrderLine {
    /** The work order — unique per line, and what React keys on. */
    key: string;
    /** PO-2026-0001, or the work order's own code where the run has no order header. */
    orderCode: string;
    productionOrderId: string | null;
    workOrderId: string;
    /** THE LINE's due date, which can differ from its order's. */
    dueDate: string;
    /** THE LINE's status. Shown per row, never rolled up — see `SkuGroup.dueDate`. */
    status: string;
    needsAcknowledgement: boolean;
    hasDocument: boolean;
    line: SiteOrderLine;
    /**
     * The order this line came from, narrowed to THIS product's lines.
     *
     * Carried whole so the card can hand it straight to `toOrderLines` without rebuilding a
     * shape the declare grid already knows how to read. Narrowed rather than passed intact so
     * that recording production from a product card offers that product and nothing else —
     * `restrictProductsToOrder` then collapses the picker to a single choice.
     */
    order: SiteOrder;
}

export interface SkuGroup {
    /**
     * THE KEY IS THE ID, NEVER THE NAME. Two SKUs can carry the same name — a renamed product,
     * a colourway entered twice — and folding on what is printed would merge two commitments
     * into one card whose figures belong to neither.
     */
    productId: string;
    productName: string;
    /**
     * The earliest date any of these orders is due, which is what decides what gets made next.
     *
     * Null only when no contributing line carries one. Each order's own date stays on its row:
     * a product wanted on two dates has no single due date, and picking the later one would
     * quietly hide the one that matters.
     */
    dueDate: string | null;
    /** The orders asking for it, in the order the server sent them — soonest due first. */
    lines: SkuOrderLine[];
    /** The seven figures, summed from `lines`. Same keys the columns are named by. */
    totals: Record<ColumnKey, number>;
    /**
     * True when ANY contributing order is still unacknowledged.
     *
     * Marks the card so a collapsed product still says something needs picking up; the badge
     * and the button stay on the order rows, where there is one order to acknowledge.
     */
    needsAcknowledgement: boolean;
}

const EMPTY_TOTALS = (): Record<ColumnKey, number> => ({
    ordered: 0,
    open: 0,
    manufactured: 0,
    qcApproved: 0,
    qcPending: 0,
    sent: 0,
    toSend: 0,
});

/**
 * Fold a site's orders into one group per product.
 *
 * INSERTION-ORDERED, so the list keeps the server's ordering — soonest due first — rather than
 * whatever order a Map happens to iterate in. The same idiom `groupQueueByProduct` uses for the
 * dispatch queue.
 *
 * BOTH REMAINDERS ARE FLOORED PER LINE BEFORE SUMMING, never after. An order 500 short on one
 * line and 200 over on another is 500 outstanding, not 300 — and a product wanted by two orders
 * has exactly that shape more often than a single order does.
 *
 * THE TWO QC FIGURES ARE SAFE TO ADD ACROSS ORDERS ONLY BECAUSE A BATCH BELONGS TO ONE WORK
 * ORDER. They are capped at what is physically on hand per line, so the sets are disjoint and
 * summing them counts no unit twice. If a batch ever became shared across orders this fold would
 * double-count in silence — which is what `skuGroups.test.ts` pins.
 */
export function buildSkuGroups(orders: SiteOrder[]): SkuGroup[] {
    const order: string[] = [];
    const byProduct = new Map<string, SkuGroup>();

    for (const siteOrder of orders) {
        for (const line of siteOrder.lines ?? []) {
            const productId = line.productId;
            if (!productId) continue;

            let group = byProduct.get(productId);
            if (!group) {
                group = {
                    productId,
                    productName: line.productName ?? productId,
                    dueDate: null,
                    lines: [],
                    totals: EMPTY_TOTALS(),
                    needsAcknowledgement: false,
                };
                byProduct.set(productId, group);
                order.push(productId);
            }

            group.lines.push({
                key: line.workOrderId,
                orderCode: siteOrder.code,
                productionOrderId: siteOrder.productionOrderId,
                workOrderId: line.workOrderId,
                dueDate: line.dueDate,
                status: line.status,
                needsAcknowledgement: siteOrder.needsAcknowledgement,
                hasDocument: siteOrder.hasDocument,
                line,
                order: {
                    ...siteOrder,
                    lines: (siteOrder.lines ?? []).filter(
                        (candidate) => candidate.productId === productId,
                    ),
                },
            });

            group.totals.ordered += line.ordered ?? 0;
            group.totals.manufactured += line.produced ?? 0;
            group.totals.qcApproved += line.qcApproved ?? 0;
            group.totals.qcPending += line.qcPending ?? 0;
            group.totals.sent += line.dispatched ?? 0;
            group.totals.open += remaining(line.ordered ?? 0, line.produced ?? 0);
            group.totals.toSend += remaining(line.ordered ?? 0, line.dispatched ?? 0);

            if (line.dueDate && (!group.dueDate || line.dueDate < group.dueDate)) {
                group.dueDate = line.dueDate;
            }
            if (siteOrder.needsAcknowledgement) group.needsAcknowledgement = true;
        }
    }

    return order.map((productId) => byProduct.get(productId)!);
}
